import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './style.scss'
import utility from 'src/utils/utility'
import { Header, BackLink, Button, CircularProgress, Text, Flex, Box, tokens, Notification } from 'src/componentv2'
import {
    OnboardingFlowSettings, AccountOpeningSettings, TransactionSettings,
    IndemnitySettings, FeatureControl, AppGeneration,
} from 'src/componentv2/Curated/BusinessLogic'
import alertTriangleIcon from 'src/assets/v2/img/alert_triangle_icon.svg'
import { useConfigurationChecklist } from 'src/app/dashboardv2/hooks/useConfigurationChecklist'
import { useSidebarOverride } from 'src/app/dashboardv2/layout/DashboardLayout'
import {PAGE_PADDING_X } from 'src/model/v2/interface/constants'

const SECTIONS = [
    'onboarding', 'account', 'transaction', 'indemnity', 'feature', 'appGeneration',
] as const
type Section = typeof SECTIONS[number]

/* the 5 real settings sections that gate progress — appGeneration is a
   confirmation step after all 5 are done, not part of the count */
const PROGRESS_SECTIONS = ['onboarding', 'account', 'transaction', 'indemnity', 'feature'] as const
type ProgressSection = typeof PROGRESS_SECTIONS[number]

const BusinessLogic = () => {
    const navigate = useNavigate()
    const { loading: checklistLoading, isComplete } = useConfigurationChecklist()
    useSidebarOverride(checklistLoading ? null : { visible: isComplete })

    const [openSection, setOpenSection] = useState<Section>('onboarding')
    const [showAppGeneration, setShowAppGeneration] = useState(false)

    /* tracks which of the 5 sections have been saved this session */
    const [completedSections, setCompletedSections] = useState<Set<ProgressSection>>(new Set())

    /* steps 1–3 already account for the first 75%; this step fills the
       remaining 25% in five equal slices, one per completed section */
    const progress = useMemo(
        () => 75 + Math.round((completedSections.size / PROGRESS_SECTIONS.length) * 25),
        [completedSections],
    )

    const allSectionsComplete = completedSections.size === PROGRESS_SECTIONS.length

    /* only shown on a fresh visit, before anything has been saved —
       disappears as soon as the first section is completed */
    const showTermsWarning = completedSections.size === 0

    /* AppGeneration only unlocks once every section — not just Feature
       Control — has been saved, regardless of the order they were done in */
    useEffect(() => {
        if (allSectionsComplete && !showAppGeneration) {
            setShowAppGeneration(true)
            setOpenSection('appGeneration')
        }
    }, [allSectionsComplete, showAppGeneration])

    const toggle = (section: Section) => (open: boolean) =>
        setOpenSection(open ? section : (undefined as unknown as Section))

    const markComplete = (section: ProgressSection, next: Section) => {
        setCompletedSections((prev) => {
            const updated = new Set(prev)
            updated.add(section)
            return updated
        })
        /* only jump to the next section if it isn't already done — avoids
           bouncing back into a completed accordion when sections are
           finished out of order */
        setOpenSection((current) => {
            if (next === 'appGeneration') return current
            return completedSections.has(next as ProgressSection) ? current : next
        })
    }

    const handleFinalSubmit = () => {
        // Only meaningful mid-onboarding — once setup is already complete
        // there's no "finish setup" step left to advance to.
        if (!isComplete) {
            navigate('/dashboardv2/setup-success')
        }
    }

    if (checklistLoading) return null

    const content = (
        <>
            {showTermsWarning && (
                <div className="business-logic__toast">
                    <Notification
                        message={
                            <>
                                Please ensure you have read and<br />
                                agreed to the terms and conditions to<br />
                                proceed
                            </>
                        }
                        actionLabel=""
                        icon={<img src={alertTriangleIcon} alt="" />}
                        duration={0}
                        visible={showTermsWarning}
                        variant="warning"
                    />
                </div>
            )}

            <Box topMargin="2" className="business-logic-list">
                <OnboardingFlowSettings
                    open={openSection === 'onboarding'}
                    onToggle={toggle('onboarding')}
                    onSaveAndNext={() => markComplete('onboarding', 'account')}
                />
                <AccountOpeningSettings
                    open={openSection === 'account'}
                    onToggle={toggle('account')}
                    onSaveAndNext={() => markComplete('account', 'transaction')}
                />
                <TransactionSettings
                    open={openSection === 'transaction'}
                    onToggle={toggle('transaction')}
                    onSaveAndNext={() => markComplete('transaction', 'indemnity')}
                />
                <IndemnitySettings
                    open={openSection === 'indemnity'}
                    onToggle={toggle('indemnity')}
                    onSaveAndNext={() => markComplete('indemnity', 'feature')}
                />
                <FeatureControl
                    open={openSection === 'feature'}
                    onToggle={toggle('feature')}
                    onSaveAndNext={() => markComplete('feature', 'appGeneration')}
                />
                {showAppGeneration && (
                    <AppGeneration open={openSection === 'appGeneration'} onToggle={toggle('appGeneration')} />
                )}
            </Box>

            {showAppGeneration && !isComplete && (
                <Flex justify="flex-end" topMargin="2.4">
                    <Button variant="primary" size="md" onClick={handleFinalSubmit}>
                        Submit
                    </Button>
                </Flex>
            )}
        </>
    )

    if (isComplete) {
        return (
            <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
                <Flex direction="column" gap="0.6" items="flex-start">
                    <Text fontSize="2.4" fontWeight="700">
                        Setup Business Logic
                    </Text>
                    <Text fontSize="1.4" color={tokens.textMuted}>
                        Configure operational rules that control customer transactions and account behavior.
                    </Text>
                </Flex>

                {content}
            </div>
        )
    }
    // ---- Still onboarding: original wizard shell ----
    return (
        <Box bgColor={tokens.pageBg} minHeight="100vh">
            <Header sticky initials="QO" />

            <div className="business-logic-page" style={{ padding: `3.2rem ${PAGE_PADDING_X}` }}>
                <BackLink onClick={() => navigate(-1)}>Go back</BackLink>

                <Flex
                    justify="space-between"
                    items="flex-start"
                    topMargin="1.6"
                    gap="1.6"
                >
                    <Box className="business-logic-header">
                        <Text
                            fontSize="2.4"
                            fontWeight="700"
                        >
                            Setup Business Logic
                        </Text>

                        <Text
                            topMargin="0.6"
                            fontSize="1.4"
                            color={tokens.textMuted}
                        >
                            Configure operational rules that control customer transactions and account behavior.
                        </Text>
                    </Box>

                    <CircularProgress
                        value={progress}
                        caption="Step 4 of 4"
                    />
                </Flex>

                {content}
            </div>
        </Box>
    )
}

export default utility.routeData('business-logic', 'Setup Business Logic', <BusinessLogic />)